import { useReportClassificationLabels } from "@/components/features/reports/use-report-classification-labels";
import { ClaimTypeFilter } from "@/components/features/reports/ClaimTypeFilter";
import { IncidentTypeFilter } from "@/components/features/reports/IncidentTypeFilter";
import {
  Card,
  CardContent,
  DataTable,
  GlobalNotAuthorized,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { ReportPresetControls } from "@/components/features/reports/ReportPresetControls";
import { ReportsPageLayout } from "@/components/features/reports/ReportsPageLayout";
import { ReportFilterBar } from "@/components/features/reports/ReportFilterBar";
import { getMatterStatusCountReportColumns } from "@/components/features/reports/MatterStatusCountReportCard/matter-status-count-report-columns";
import { useReportFilterPresets } from "@/hooks/use-report-filter-presets";
import { retrieveReportsMatterStatusCountQueryOptions } from "@/lib/data/react-query/reports/reports-matter-status-count";
import { REPORT_FILTER_PRESET_TYPES } from "@/lib/data/static/report-filter-preset-types";
import { MATTER_STATUS_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { type ReportsMatterStatusCount } from "@/lib/types";
import {
  normalizePresetStringArray,
  toReportPresetPreviewFilter,
} from "@/lib/utils/report-filter-presets";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { FolderOpen } from "lucide-react";
import startCase from "lodash/startCase";
import { useMemo, useState } from "react";
import { z } from "zod";

export const Route = createFileRoute("/_auth/reports/matter-status-count")({
  component: RouteComponent,
  validateSearch: z.object({
    status: z.string().optional(),
    incidentType: z.string().optional(),
    claimType: z.string().optional(),
  }),
});

type StatusCategory = (typeof MATTER_STATUS_CATEGORY_OPTIONS)[number];

const MATTER_STATUS_COUNT_REPORT_TABLE_NAME =
  tanstackTableNames.reportsMatterStatusCount;
const MATTER_STATUS_COUNT_REPORT_PRESET_COLUMN_IDS = {
  category: "Category",
  numberOfMatters: "Number of Matters",
  status: "Status",
} as const;
const MATTER_STATUS_COUNT_REPORT_PRESET_COLUMN_KEYS = Object.fromEntries(
  Object.entries(MATTER_STATUS_COUNT_REPORT_PRESET_COLUMN_IDS).map(
    ([key, value]) => [value, key],
  ),
) as Record<string, keyof typeof MATTER_STATUS_COUNT_REPORT_PRESET_COLUMN_IDS>;

const parseSearchValues = (value?: string) => {
  if (!value) {
    return [];
  }
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const serializeSearchValues = (values: string[]) =>
  values.length ? values.join(",") : undefined;

const buildSearchFromPreset = (
  rawFilters: Record<string, unknown>,
): Record<string, string | undefined> => ({
  incidentType: serializeSearchValues(
    normalizePresetStringArray(rawFilters.incidentTypeIds),
  ),
  claimType: serializeSearchValues(
    normalizePresetStringArray(rawFilters.claimTypeIds),
  ),
  status: serializeSearchValues(normalizePresetStringArray(rawFilters.status)),
});

function RouteComponent() {
  const { hasPagePermission } = useUser();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const selectedIncidentTypes = useMemo(
    () => parseSearchValues(search.incidentType),
    [search.incidentType],
  );
  const selectedClaimTypes = useMemo(
    () => parseSearchValues(search.claimType),
    [search.claimType],
  );
  const [statusSelectValue, setStatusSelectValue] = useState<
    string | undefined
  >();

  const canViewReport = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersReportsPerStatus,
  );

  const classificationFilters = useReportClassificationLabels(
    selectedIncidentTypes,
    selectedClaimTypes,
    canViewReport,
  );

  const selectedStatuses = useMemo(
    () => parseSearchValues(search.status) as StatusCategory[],
    [search.status],
  );

  const isQueryEnabled = canViewReport && selectedStatuses.length > 0;
  const { data, isLoading, error } = useQuery({
    ...retrieveReportsMatterStatusCountQueryOptions(selectedStatuses, {
      incidentTypeIds: selectedIncidentTypes,
      claimTypeIds: selectedClaimTypes,
    }),
    enabled: isQueryEnabled,
  });

  const columns = useMemo(
    () => getMatterStatusCountReportColumns(selectedStatuses),
    [selectedStatuses],
  );
  const matterStatusCountPresetColumnKeysById = useMemo(
    () =>
      columns.reduce<Record<string, string>>((acc, column) => {
        if (typeof column.id !== "string") return acc;
        if (!(column.id in MATTER_STATUS_COUNT_REPORT_PRESET_COLUMN_KEYS)) {
          return acc;
        }
        acc[column.id] =
          MATTER_STATUS_COUNT_REPORT_PRESET_COLUMN_KEYS[column.id];
        return acc;
      }, {}),
    [columns],
  );

  const reportRows = useMemo<ReportsMatterStatusCount[]>(() => {
    const rows = selectedStatuses.length > 0 ? (data?.data ?? []) : [];
    return [...rows].sort((a, b) => b.count - a.count);
  }, [data?.data, selectedStatuses.length]);

  const totalMatters = useMemo(
    () => reportRows.reduce((sum, row) => sum + Number(row.count), 0),
    [reportRows],
  );

  const statusLabel = useMemo(() => {
    if (selectedStatuses.length === 1) {
      return startCase(selectedStatuses[0]);
    }
    if (selectedStatuses.length === 0) {
      return undefined;
    }
    return "Selected";
  }, [selectedStatuses]);
  const addUniqueValue = (values: string[], value: string) =>
    values.includes(value) ? values : [...values, value];
  const presetPreviewFilters = useMemo(
    () => [
      ...classificationFilters.map((filter) =>
        toReportPresetPreviewFilter(filter.label),
      ),
      ...selectedStatuses.map((status) =>
        toReportPresetPreviewFilter(`Status: ${startCase(status)}`),
      ),
    ],
    [selectedStatuses, classificationFilters],
  );

  const {
    applyPresetByValue,
    clearSelectedPreset,
    createReportFilterPresetMutation,
    deletePresetByValue,
    deleteReportFilterPresetMutation,
    handleColumnOrderChange,
    handleColumnVisibilityChange,
    isReportFilterPresetsLoading,
    isSavePresetOpen,
    presetDescription,
    presetName,
    presetOptions,
    presetSelectValue,
    savePreset,
    savePreviewColumns,
    setIsSavePresetOpen,
    setPresetDescription,
    setPresetName,
    tableRenderKey,
  } = useReportFilterPresets({
    availableColumnIds: columns
      .map((column) => column.id)
      .filter((columnId): columnId is string => typeof columnId === "string"),
    buildSearchFromPreset,
    currentFilterOptions: {
      incidentTypeIds: selectedIncidentTypes,
      claimTypeIds: selectedClaimTypes,
      status: selectedStatuses,
    },
    enabled: canViewReport,
    lockedVisibleColumnIds: [
      MATTER_STATUS_COUNT_REPORT_PRESET_COLUMN_IDS.category,
      MATTER_STATUS_COUNT_REPORT_PRESET_COLUMN_IDS.status,
    ],
    onApplySearch: async (nextSearch) => {
      await navigate({
        search: () => nextSearch,
        replace: true,
      });
    },
    presetKeyByColumnId: matterStatusCountPresetColumnKeysById,
    reportType: REPORT_FILTER_PRESET_TYPES.matterStatusCount,
    tableName: MATTER_STATUS_COUNT_REPORT_TABLE_NAME,
  });
  const getMatterStatusCountCsvValue = (
    row: ReportsMatterStatusCount,
    columnId: string,
  ) => {
    switch (columnId) {
      case "Category":
        return row.category
          ? startCase(row.category)
          : selectedStatuses.length === 1
            ? startCase(selectedStatuses[0])
            : selectedStatuses.length > 1
              ? "Multiple"
              : "-";
      case "Status":
        return row.label;
      case "Number of Matters":
        return row.count.toLocaleString();
      default:
        return "";
    }
  };

  if (!canViewReport) {
    return <GlobalNotAuthorized />;
  }

  return (
    <ReportsPageLayout
      title="Matter status count report"
      headerActions={
        <ReportPresetControls
          isDeleting={deleteReportFilterPresetMutation.isPending}
          isPresetListLoading={isReportFilterPresetsLoading}
          isSaveOpen={isSavePresetOpen}
          isSaving={createReportFilterPresetMutation.isPending}
          onDeletePresetValue={(value) => {
            void deletePresetByValue(value);
          }}
          onPresetDescriptionChange={setPresetDescription}
          onPresetNameChange={setPresetName}
          onPresetValueChange={(value) => {
            void applyPresetByValue(value);
          }}
          onSave={() => {
            void savePreset();
          }}
          onSaveOpenChange={setIsSavePresetOpen}
          presetDescription={presetDescription}
          presetName={presetName}
          presetOptions={presetOptions}
          presetValue={presetSelectValue}
          savePreviewColumns={savePreviewColumns}
          savePreviewFilters={presetPreviewFilters}
          saveTitle="Save matter status count report preset"
          selectId="matter-status-count-report-preset-select"
        />
      }
    >
      <ReportFilterBar
        appliedFilters={[
          ...classificationFilters.map((filter) => ({
            key: `${filter.dimension}:${filter.value}`,
            label: filter.label,
            onRemove: () => {
              clearSelectedPreset();
              const values =
                filter.dimension === "incidentType"
                  ? selectedIncidentTypes
                  : selectedClaimTypes;
              void navigate({
                search: (prev) => ({
                  ...prev,
                  [filter.dimension]: serializeSearchValues(
                    values.filter((value) => value !== filter.value),
                  ),
                }),
                replace: true,
              });
            },
          })),
          ...selectedStatuses.map((status) => ({
            key: `status-${status}`,
            label: startCase(status),
            onRemove: () => {
              clearSelectedPreset();
              const nextStatuses = selectedStatuses.filter(
                (item) => item !== status,
              );
              void navigate({
                search: (prev) => ({
                  ...prev,
                  status: serializeSearchValues(nextStatuses),
                }),
                replace: true,
              });
            },
          })),
        ]}
        onClearFilters={async () => {
          clearSelectedPreset();
          setStatusSelectValue(undefined);
          await navigate({
            search: () => ({
              status: serializeSearchValues([]),
            }),
            replace: true,
          });
        }}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <IncidentTypeFilter
            value={selectedIncidentTypes}
            onChange={(values) => {
              clearSelectedPreset();
              void navigate({
                search: (prev) => ({
                  ...prev,
                  incidentType: serializeSearchValues(values),
                }),
                replace: true,
              });
            }}
          />
          <ClaimTypeFilter
            value={selectedClaimTypes}
            onChange={(values) => {
              clearSelectedPreset();
              void navigate({
                search: (prev) => ({
                  ...prev,
                  claimType: serializeSearchValues(values),
                }),
                replace: true,
              });
            }}
          />
          <Select
            value={statusSelectValue ?? ""}
            onValueChange={async (value) => {
              const next = addUniqueValue(selectedStatuses, value);
              setStatusSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  status: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger
              className="h-11 w-[220px]"
              aria-label="Select matter status category"
            >
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              {MATTER_STATUS_CATEGORY_OPTIONS.map((status) => (
                <SelectItem key={status} value={status}>
                  {startCase(status)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </ReportFilterBar>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card className="border-border/60 shadow-sm">
          <CardContent className="space-y-2 pt-4">
            {isLoading ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="bg-muted h-10 w-10 animate-pulse rounded-full" />
                  <div className="bg-muted h-4 w-32 animate-pulse rounded" />
                </div>
                <div className="bg-muted h-7 w-20 animate-pulse rounded" />
              </>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <div className="bg-muted text-foreground flex h-10 w-10 items-center justify-center rounded-full">
                    <FolderOpen className="h-5 w-5" />
                  </div>
                  <Text variant="subHeading">
                    {statusLabel
                      ? `Total ${statusLabel.toLowerCase()} matters`
                      : "Total matters"}
                  </Text>
                </div>
                <Text variant="title" className="text-2xl leading-8">
                  {totalMatters.toLocaleString()}
                </Text>
              </>
            )}
          </CardContent>
        </Card>
      </div>
      <div className="space-y-4">
        <DataTable
          entityLabelPlural="statuses"
          key={tableRenderKey}
          hideSearchBar
          columns={columns}
          data={reportRows}
          onColumnOrderChange={handleColumnOrderChange}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          onRowClick={() => {}}
          tableName={MATTER_STATUS_COUNT_REPORT_TABLE_NAME}
          isLoading={isLoading}
          isError={Boolean(error)}
          noResultsMessage={
            selectedStatuses.length === 0
              ? "Please select a category to view matter status counts"
              : "No status counts found for the selected category."
          }
          leftToolbarContent={
            <Text variant="body" className="text-lg font-semibold">
              Matters per status
            </Text>
          }
          exportCsv={{
            fileName: "matter-status-count-report.csv",
            getCellValue: getMatterStatusCountCsvValue,
          }}
        />
      </div>
    </ReportsPageLayout>
  );
}
